import { useMemo, useState, type ReactNode } from 'react';
import { CustomerHero } from '@/components/CustomerHero';
import { SectionCard } from '@/components/DataList';
import { Icon } from '@/components/icons';
import { products } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import {
  Button,
  Empty,
  ErrorBox,
  Form,
  Input,
  Loading,
  Modal,
  Money,
} from '@/components/ui';
import { required, str, uuid } from '@/utils/format';
import { mediaUrl } from '@/utils/mediaUrl';

function productPriceLabel(p: JsonMap): ReactNode {
  const price = p.price ?? p.sale_price ?? p.sell_price ?? p.min_price;
  if (price !== undefined && price !== null && price !== '') {
    return <Money value={price} suffix="$" />;
  }
  return 'السعر غير محدد';
}

function ProductCard({
  product,
  onSelect,
}: {
  product: JsonMap;
  onSelect: (p: JsonMap) => void;
}) {
  const img = mediaUrl(product.category_img ?? product.image_url ?? product.image);
  const shape = str(product.image_shape, 'square');
  return (
    <button type="button" className="product-card click-card" onClick={() => onSelect(product)}>
      <div className={`product-img shape-${shape}`}>{img ? <img src={img} alt="" /> : null}</div>
      <strong>{str(product.name ?? product.title)}</strong>
      <span className="product-card-price">{productPriceLabel(product)}</span>
    </button>
  );
}

function ProductsBody({
  categoryId,
  onOpenCategory,
  onBack,
}: {
  categoryId: string | null;
  onOpenCategory: (id: string) => void;
  onBack: () => void;
}) {
  const [selected, setSelected] = useState<JsonMap | null>(null);
  const [playerId, setPlayerId] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const state = useAsync(
    () =>
      products.content(categoryId ? { category_id: Number(categoryId) || categoryId } : undefined),
    (p) => {
      const root = p.data && typeof p.data === 'object' ? (p.data as JsonMap) : p;
      return {
        current: root.current && typeof root.current === 'object' ? (root.current as JsonMap) : null,
        categories: pickList(p, 'categories', 'items'),
        products: pickList(p, 'products', 'items', 'data'),
      };
    },
  );

  const categories = state.data?.categories ?? [];
  const listProducts = state.data?.products ?? [];
  const current = state.data?.current;
  const insideCategory = categoryId !== null;
  const gridHasItems = categories.length + listProducts.length > 0;

  const title = useMemo(() => {
    if (!insideCategory) return 'تصفح الفئات';
    return str(current?.name ?? current?.title, 'المنتجات');
  }, [current, insideCategory]);

  async function order() {
    if (!selected) return;
    const miss = required({ playerId }, ['playerId']);
    if (miss) {
      setMsg(miss);
      return;
    }
    setBusy(true);
    setMsg('');
    const res = await products.order({
      product_id: selected.id ?? selected.product_id,
      target_value: playerId,
      order_uuid: uuid(),
      qty: 1,
    });
    setBusy(false);
    if (!res.success) {
      setMsg(errorMessage(res));
      return;
    }
    setMsg(typeof res.message === 'string' ? res.message : 'تم إرسال طلب المنتج.');
    setPlayerId('');
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <>
      <SectionCard
        title={title}
        subtitle={
          insideCategory
            ? 'اختر المنتج المطلوب لإتمام الطلب.'
            : 'اختر القسم لعرض المنتجات المتاحة.'
        }
        icon={<Icon name="game" size={24} />}
        className="customer-products-card"
      >
        {insideCategory ? (
          <button type="button" className="btn btn-secondary back-btn" onClick={onBack}>
            رجوع
          </button>
        ) : null}

        <div className="products-grid">
          {categories.map((c) => {
            const img = mediaUrl(c.image_url ?? c.image ?? c.category_img);
            return (
              <button
                key={str(c.id ?? c.category_id)}
                type="button"
                className="category-card click-card"
                onClick={() => onOpenCategory(str(c.id ?? c.category_id, ''))}
              >
                <div className="category-img">
                  {img ? <img src={img} alt="" /> : <strong>{str(c.name ?? c.title).slice(0, 1)}</strong>}
                </div>
                <strong>{str(c.name ?? c.title)}</strong>
              </button>
            );
          })}
          {listProducts.map((p) => (
            <ProductCard key={str(p.id ?? p.product_id)} product={p} onSelect={setSelected} />
          ))}
        </div>

        {!gridHasItems ? (
          <Empty
            title="لا توجد منتجات"
            hint={
              insideCategory
                ? 'لا توجد منتجات ظاهرة في هذا القسم. تأكد من تفعيلها وتعيين السعر من الإدارة.'
                : 'لم تتم إضافة أقسام أو منتجات متاحة بعد.'
            }
          />
        ) : null}
      </SectionCard>

      <Modal open={!!selected} title={str(selected?.name, 'طلب منتج')} onClose={() => setSelected(null)}>
        <Form className="form-grid" onSubmit={() => void order()}>
          <Input
            label="معرّف اللاعب / الحساب"
            value={playerId}
            onChange={(e) => setPlayerId(e.target.value)}
            required
          />
          <div className="order-summary checkout-summary">
            <span>المبلغ المطلوب خصمه من محفظة الألعاب</span>
            <strong>
              <Money
                value={selected?.price ?? selected?.sale_price ?? selected?.sell_price}
                suffix="$"
              />
            </strong>
          </div>
          {msg ? <p className={msg.includes('تم') ? 'ok' : 'error'}>{msg}</p> : null}
          <Button type="submit" className="big-action" disabled={busy}>
            {busy ? 'جاري التنفيذ...' : 'تنفيذ الطلب'}
          </Button>
        </Form>
      </Modal>
    </>
  );
}

export function ProductsScreen() {
  const [categoryId, setCategoryId] = useState<string | null>(null);

  return (
    <div className="customer-page products-page games-v12-page">
      <CustomerHero
        placement="games"
        fallbackTitle="اشحنلي ألعاب"
        fallbackText="اختر اللعبة أو المنتج المطلوب واستمتع بتجربة لعب مميزة."
        icon="game"
      />
      <ProductsBody
        key={categoryId ?? 'root'}
        categoryId={categoryId}
        onOpenCategory={setCategoryId}
        onBack={() => setCategoryId(null)}
      />
    </div>
  );
}
